import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, filter, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { CheckBoxList } from '../shared/models/notification';
import { MessageService, SlbSeverity } from '@slb-dls/angular-material/notification';
import { CameraProfileService } from '../shared/services/camera-profile.service';
import { WorkFlow } from '../shared/models/global-view';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { SirenService } from '../shared/services/siren.service';
import { SirenDetail } from '../shared/models/siren';
import { SirenConstant } from '../shared/constants/siren-constant';
import { TimeZone } from '../shared/models/dashabordAlertData';
import { TimeZoneService } from '../shared/services/time-zone.service';

@Component({
  selector: 'app-siren-popover',
  templateUrl: './siren-popover.component.html',
  styleUrls: ['./siren-popover.component.scss'],
})
export class SirenPopoverComponent implements OnInit, OnDestroy {
  public workflows: CheckBoxList[];
  public controllerId: string;
  public isLoading: boolean;
  public sirenSaveStatusMessage: string;
  public deviceOffline: boolean;
  public cameraOfflineErrorMsg = SirenConstant.CAMERA_DISCONNECTED_MSG;
  public disableApply: boolean;
  public currentZoneDetails!: TimeZone;
  public savedSirenDetails: SirenDetail;

  private gatewayStatus: string;
  private destroyed = new Subject();

  constructor(
    private messageService: MessageService,
    private sirenService: SirenService,
    private cameraProfileService: CameraProfileService,
    private timeZoneService: TimeZoneService,
    @Inject(MAT_DIALOG_DATA) public data: { controllerId: string; cameraStatus: string },
    private dialogRef: MatDialogRef<SirenPopoverComponent>
  ) {
    this.timeZoneService.timeZoneDetails$
      .pipe(
        filter((zone: TimeZone) => !!Object.keys(zone).length),
        tap((timeZoneDetails: TimeZone) => {
          this.currentZoneDetails = timeZoneDetails;
          this.timeZoneService.setSelectedTimeZone(this.currentZoneDetails);
          this.currentZoneDetails.timezone = this.timeZoneService.getTimeZone();
        }),
        takeUntil(this.destroyed)
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.disableApply = true;
    this.getStatusMessage();
    this.gatewayStatus = sessionStorage.getItem('gatewayStatus') ?? '';
    this.showOfflineToasterWhenCameraOffline();
    this.getSirenWorkflows();
  }

  getSirenWorkflows(): void {
    this.cameraProfileService
      .getWorkFlowByControllerId(this.data.controllerId)
      .pipe(
        tap((controllerWorkflows: WorkFlow[]) => {
          this.workflows = controllerWorkflows;
          this.workflows.forEach(workflow => (workflow.isChecked = false));
          this.workflows.forEach(workFlow => {
            SirenConstant.sirenWorkFlowItems.forEach(s => {
              if (s.name.toLowerCase().split(' ').join('').includes(workFlow.name.toLowerCase().split(' ').join(''))) {
                workFlow.name = s.name;
              }
            });
          });
        }),
        switchMap(() => {
          return this.sirenService.getCommandStatusForCurrentWorkflows(this.data.controllerId, false).pipe(
            tap((savedWorkflowDetails: SirenDetail) => {
              if (savedWorkflowDetails?.workFlowIds) {
                this.savedSirenDetails = savedWorkflowDetails;
                if (savedWorkflowDetails?.sirenStatus.toLowerCase() === 'error') {
                  this.sirenService.setUpdateStatusMessage('error');
                  this.disableApply = false;
                } else if (savedWorkflowDetails?.sirenStatus.toLowerCase() === 'inprogress') {
                  this.sirenService.setUpdateStatusMessage('inprogress');
                }
                savedWorkflowDetails?.workFlowIds.forEach((workFlowId: string) => {
                  this.workflows.forEach(w => {
                    if (w.id === workFlowId) {
                      w.isChecked = true;
                    }
                  });
                });
              }
              this.isLoading = false;
            }),
            takeUntil(this.destroyed)
          );
        }),
        catchError(() => {
          this.messageService.add({
            severity: SlbSeverity.Error,
            summary: SirenConstant.GET_SIREN_DETAIL_ERROR,
            closable: true,
            sticky: true,
          });

          return of<any[]>({} as any[]);
        }),
        takeUntil(this.destroyed)
      )
      .subscribe();
  }

  public onWorkflowChange(event: MatCheckboxChange, selectedWorkflow: CheckBoxList): void {
    selectedWorkflow.isChecked = event.checked;
    this.disableApply = false;
  }

  public saveWorkflow(): void {
    const requestPayload = {
      equipmentId: this.data.controllerId,
      workFlowIds: this.workflows.filter(w => w.isChecked).map(wF => wF.id),
    };
    const message = requestPayload.workFlowIds?.length > 0 ? SirenConstant.UPDATE_IN_PROGRESS_MSG : SirenConstant.DISABLED_SUCCESS_MSG;
    this.sirenService.setUpdateStatusMessage('inprogress');
    this.messageService.add({
      severity: SlbSeverity.Info,
      summary: message,
      closable: true,
      sticky: false,
      life: 7000,
    });
    this.sirenService.setUpdatedSirenDetails(requestPayload);
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroyed.next(true);
    this.destroyed.complete();
  }

  private getStatusMessage(): void {
    this.sirenService.updateStatusMessage$.subscribe(message => {
      this.sirenSaveStatusMessage = message;
      if (message === 'error') {
        this.sirenSaveStatusMessage = SirenConstant.UPDATE_ERROR_MSG;
        this.disableApply = false;
      } else if (message === 'inprogress') {
        this.sirenSaveStatusMessage = SirenConstant.UPDATE_IN_PROGRESS_MSG;
        this.disableApply = true;
      } else {
        this.sirenSaveStatusMessage = '';
        this.disableApply = true;
      }
    });
  }

  private showOfflineToasterWhenCameraOffline(): void {
    if (this.data.cameraStatus !== '0' || this.gatewayStatus !== '0') {
      this.deviceOffline = true;
    } else {
      this.deviceOffline = false;
    }
  }
}
